'use client'

import { EventCategory } from '@tebuto/api/therapists/models/EventCategory'
import type { EventRulePayload } from '@tebuto/api/therapists/models/EventRulePayload'
import {
    convertMeetingToFullCalendarEvent,
    convertRuleEventToFullCalendarEvent,
    convertSeminarSessionToFullCalendarEvent,
    convertStandaloneEventToFullCalendarEvent,
    convertThirdPartyEventToFullCalendarEvent,
    FullCalendarEvent,
    ParsedMeetingEvent,
    ParsedSeminarEvent,
    ParsedTebutoRuleEvent,
    ParsedTebutoStandaloneEvent,
    ParsedThirdPartyEvent
} from '@tebuto/functions/calendar'
import { filterCalendarRuleEvents } from '@tebuto/functions/calendar-rule-filters'
import {
    useSelectedTherapist,
    useSelectedTherapistId,
    useTherapistsApiMutation,
    useTherapistsApiRequest
} from '@tebuto/functions/hooks'
import { invalidateTherapistMeetingsCache, meetingOverlapsVisibleRange } from '@tebuto/functions/meetings-cache'
import { fetchMeetingsInRange } from '@tebuto/functions/meetings-range'
import { useAppTranslation } from '@tebuto/functions/translation.page'
import { DateTime } from 'luxon'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useSWRConfig } from 'swr'
import { CalendarContext } from './CalendarContext'
import {
    buildGeneratedEventRuleSlots,
    EventRuleForm,
    getEffectiveEventRuleInterval,
    getEventRuleStartDateTime,
    getSelectedEventRuleCategoryIds,
    isGeneratedEventRuleLimitExceeded,
    MAX_GENERATED_EVENT_RULES
} from './event-rule-form'

export function useCalendarData() {
    const therapistId = useSelectedTherapistId()
    const { mutate: revalidateSwrCache } = useSWRConfig()
    const { calendarConfiguration, startDate, endDate, highlightedEventStart } = useContext(CalendarContext)
    const [fullCalendarTebutoEvents, setFullCalendarTebutoEvents] = useState<
        FullCalendarEvent<ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent>[]
    >([])
    const [fullCalendarThirdPartyEvents, setFullCalendarThirdPartyEvents] = useState<
        FullCalendarEvent<ParsedThirdPartyEvent>[]
    >([])
    const [fullCalendarMeetingEvents, setFullCalendarMeetingEvents] = useState<FullCalendarEvent<ParsedMeetingEvent>[]>(
        []
    )
    const [fullCalendarSeminarEvents, setFullCalendarSeminarEvents] = useState<FullCalendarEvent<ParsedSeminarEvent>[]>(
        []
    )

    const {
        data: openings,
        showLoading: showOpeningsLoading,
        error: openingsError,
        mutate: mutateOpenings
    } = useTherapistsApiRequest(['openings', therapistId], (api) => api.openings.getAllOpenings(therapistId))
    const {
        data: eventRules,
        showLoading: showEventRulesLoading,
        error: eventRulesError,
        mutate: mutateEventRules
    } = useTherapistsApiRequest(['eventRules', therapistId], (api) => api.eventRules.getAllEventRules(therapistId))
    const {
        data: eventOverview,
        showLoading: showEventOverviewLoading,
        error: eventsError,
        mutate: mutateEventOverview
    } = useTherapistsApiRequest(
        ['events', therapistId, startDate.toISO() ?? '', endDate.toISO() ?? ''],
        (api, [_name, therapistId, start, end]) => {
            if (start && end) {
                return api.events.getEventOverview(therapistId as number, start as string, end as string)
            }
        },
        { refreshInterval: 60000 }
    )
    const {
        data: meetingList,
        showLoading: showMeetingsLoading,
        error: meetingsError
    } = useTherapistsApiRequest(
        ['meetings', therapistId, 'calendar', startDate.toISO() ?? '', endDate.toISO() ?? ''],
        (api) => {
            const start = startDate.toISO()
            const end = endDate.toISO()
            if (!start || !end) {
                return { meetings: [], total: 0 }
            }
            return fetchMeetingsInRange((page, pageSize) =>
                api.meetings.getMeetings(therapistId, undefined, undefined, true, start, end, pageSize, page)
            )
        }
    )
    const { data: therapist, showLoading: showTherapistLoading, error: therapistError } = useSelectedTherapist()
    const seminarsEnabled = therapist?.features?.featureSeminarsEnabled === true
    const seminarSessionsKey = seminarsEnabled
        ? (['seminarSessions', therapistId, startDate.toISO() ?? '', endDate.toISO() ?? ''] as [
              'seminarSessions',
              number,
              string,
              string
          ])
        : null
    const {
        data: seminarSessions,
        showLoading: showSeminarSessionsLoading,
        error: seminarSessionsError,
        mutate: mutateSeminarSessions
    } = useTherapistsApiRequest(seminarSessionsKey, (api, key) => {
        if (!key) {
            return
        }
        const [, requestTherapistId, start, end] = key
        if (start && end) {
            return api.seminars.getCalendarSeminarSessions(requestTherapistId, start, end)
        }
    })
    const {
        eventCategoryMapping,
        showLoading: showEventCategoriesLoading,
        error: eventCategoriesError
    } = useEventCategoryMapping()
    const { t } = useAppTranslation()

    useEffect(() => {
        if (eventOverview && Object.keys(eventCategoryMapping).length > 0 && eventRules) {
            const ruleEvents = eventOverview.rules
                .map((event) =>
                    convertRuleEventToFullCalendarEvent(
                        event,
                        eventCategoryMapping,
                        eventRules,
                        highlightedEventStart,
                        { flexibleSlotCalendarTitle: t('calendarPage.flexibleSpotCalendarTitle') }
                    )
                )
                .filter((event) => event !== null) as FullCalendarEvent<ParsedTebutoRuleEvent>[]
            const standaloneEvents = eventOverview.standalone.map((event) =>
                convertStandaloneEventToFullCalendarEvent(event, eventCategoryMapping, eventRules)
            )
            const thirdPartyEvents = eventOverview.thirdParty.map((event) =>
                convertThirdPartyEventToFullCalendarEvent(event)
            )

            setFullCalendarTebutoEvents([
                ...filterCalendarRuleEvents(ruleEvents, {
                    showFreeAppointments: calendarConfiguration.showFreeAppointments,
                    showBookedAppointments: calendarConfiguration.showBookedAppointments
                }),
                ...standaloneEvents
            ])
            setFullCalendarThirdPartyEvents(thirdPartyEvents)
        }
    }, [
        eventOverview,
        eventCategoryMapping,
        eventRules,
        calendarConfiguration.showFreeAppointments,
        calendarConfiguration.showBookedAppointments,
        highlightedEventStart,
        t
    ])

    useEffect(() => {
        if (!meetingList?.meetings) {
            return
        }
        const visibleMeetings = meetingList.meetings.filter((meeting) =>
            meetingOverlapsVisibleRange(meeting, startDate, endDate)
        )
        setFullCalendarMeetingEvents(visibleMeetings.map(convertMeetingToFullCalendarEvent))
    }, [meetingList, startDate, endDate])

    useEffect(() => {
        if (!seminarsEnabled) {
            setFullCalendarSeminarEvents([])
            return
        }
        if (!seminarSessions?.sessions) {
            return
        }
        setFullCalendarSeminarEvents(seminarSessions.sessions.map(convertSeminarSessionToFullCalendarEvent))
    }, [seminarSessions, seminarsEnabled])

    return {
        openings,
        therapist,
        fullCalendarTebutoEvents,
        fullCalendarThirdPartyEvents,
        fullCalendarMeetingEvents,
        fullCalendarSeminarEvents,
        showLoading:
            showOpeningsLoading ||
            showEventRulesLoading ||
            showEventCategoriesLoading ||
            showEventOverviewLoading ||
            showMeetingsLoading ||
            showSeminarSessionsLoading ||
            showTherapistLoading,
        error:
            openingsError ||
            eventRulesError ||
            eventCategoriesError ||
            eventsError ||
            meetingsError ||
            seminarSessionsError ||
            therapistError,
        mutate: () => {
            mutateOpenings()
            mutateEventRules()
            mutateEventOverview()
            void mutateSeminarSessions()
            void invalidateTherapistMeetingsCache(revalidateSwrCache, therapistId)
        }
    }
}

export function useEventCategoryMapping() {
    const { eventCategories, showLoading, error } = useEventCategories()
    const [eventCategoryMapping, setEventCategoryMapping] = useState<Record<number, EventCategory>>({})

    useEffect(() => {
        if (eventCategories) {
            setEventCategoryMapping(
                eventCategories.reduce(
                    (acc, category) => {
                        acc[category.id] = category
                        return acc
                    },
                    {} as Record<number, EventCategory>
                )
            )
        }
    }, [eventCategories])

    return {
        eventCategoryMapping,
        showLoading,
        error,
        categoriesLoaded: eventCategories !== undefined,
        hasCategories: (eventCategories?.length ?? 0) > 0
    }
}

export function useEventCategories() {
    const therapistId = useSelectedTherapistId()
    const {
        data: eventCategories,
        showLoading,
        error,
        mutate
    } = useTherapistsApiRequest(['eventCategories', therapistId], (api) =>
        api.eventCategories.getAllEventCategories(therapistId)
    )
    return { eventCategories, showLoading, error, mutate }
}

// Mutations

export function useGetAddEventRuleConflicts() {
    const therapistId = useSelectedTherapistId()
    const { trigger: getAddEventRuleConflicts, isMutating: isGetAddEventRuleConflictsMutating } =
        useTherapistsApiMutation(
            ['getAddEventRuleConflicts', therapistId],
            (api, { values, start }: { values: EventRuleForm; start?: DateTime<true> }) =>
                api.eventRules.getAddEventRuleConflicts(therapistId, buildEventRulePayload(values, start)),
            { successMessage: null }
        )

    return { getAddEventRuleConflicts, isGetAddEventRuleConflictsMutating }
}

interface AddGeneratedEventRulesOptions {
    values: EventRuleForm
    eventCategories: EventCategory[]
}

interface AddGeneratedEventRulesResult {
    createdCount: number
    skippedCount: number
    conflicts: string[]
}

export function useCheckEventRuleMoveOptions(event: ParsedTebutoRuleEvent) {
    const therapistId = useSelectedTherapistId()

    const { trigger: checkEventRuleMoveOptions, isMutating: isCheckEventRuleMoveOptionsMutating } =
        useTherapistsApiMutation(
            ['checkEventRuleMoveOptions', therapistId, event.start],
            async (api, { start, endDate }: { start: DateTime<true>; endDate: DateTime<true> | null }) =>
                api.eventRules.getEventRuleMoveOptions(therapistId, {
                    eventRuleId: event.rule.id,
                    diffMilliseconds: start.diff(event.start).milliseconds,
                    selectedDate: event.start.startOf('day'),
                    newEndDate: endDate?.toISODate(),
                    alwaysReturnOptions: true
                }),
            { successMessage: null }
        )

    return { checkEventRuleMoveOptions, isCheckEventRuleMoveOptionsMutating }
}

export function useAddEventRule() {
    const therapistId = useSelectedTherapistId()
    const { setIsAddEventRuleModalOpen } = useContext(CalendarContext)
    const refreshCalendarData = useRefreshCalendarData()
    const { trigger: addEventRule, isMutating: isAddEventRuleMutating } = useTherapistsApiMutation(
        ['addEventRule', therapistId],
        (api, values: EventRuleForm) => api.eventRules.addEventRule(therapistId, buildEventRulePayload(values)),
        {
            onSuccess: () => {
                refreshCalendarData(therapistId)
                setIsAddEventRuleModalOpen(false)
            }
        }
    )

    return { addEventRule, isAddEventRuleMutating }
}

export function useAddGeneratedEventRules(options?: {
    onGenerated?: (result: AddGeneratedEventRulesResult) => void
    suppressModalClose?: boolean
}) {
    const therapistId = useSelectedTherapistId()
    const calendarContext = useContext(CalendarContext)
    const setIsAddEventRuleModalOpen =
        'setIsAddEventRuleModalOpen' in calendarContext ? calendarContext.setIsAddEventRuleModalOpen : undefined
    const refreshCalendarData = useRefreshCalendarData()
    const { trigger: addGeneratedEventRules, isMutating: isAddGeneratedEventRulesMutating } = useTherapistsApiMutation<
        AddGeneratedEventRulesResult,
        AddGeneratedEventRulesOptions
    >(
        ['addGeneratedEventRules', therapistId],
        async (api, { values, eventCategories }) => {
            const selectedCategoryIds = new Set(getSelectedEventRuleCategoryIds(values))
            const selectedCategories = eventCategories.filter((category) => selectedCategoryIds.has(category.id))
            const longestSelectedDuration = Math.max(...selectedCategories.map((category) => category.duration), 0)
            if (selectedCategories.length === 0) {
                return { createdCount: 0, skippedCount: 0, conflicts: ['Bitte wählen Sie eine Terminkategorie aus.'] }
            }

            const slots = buildGeneratedEventRuleSlots(values, longestSelectedDuration)
            if (slots.length === 0) {
                return {
                    createdCount: 0,
                    skippedCount: 0,
                    conflicts: ['In diesem Zeitraum passt kein Termin mit der gewählten Dauer.']
                }
            }
            if (isGeneratedEventRuleLimitExceeded(values, longestSelectedDuration)) {
                return {
                    createdCount: 0,
                    skippedCount: 0,
                    conflicts: [`Der Zeitraum erzeugt mehr als ${MAX_GENERATED_EVENT_RULES} Termine.`]
                }
            }

            const conflicts: string[] = []
            const slotsToCreate: typeof slots = []

            for (const slot of slots) {
                const result = await api.eventRules.getAddEventRuleConflicts(
                    therapistId,
                    buildEventRulePayload(values, slot.start)
                )
                conflicts.push(
                    ...result.conflicts.map((conflict) => `${slot.start.toFormat('HH:mm')} Uhr: ${conflict}`)
                )

                if (result.conflicts.length === 0) {
                    slotsToCreate.push(slot)
                }
            }

            for (const slot of slotsToCreate) {
                await api.eventRules.addEventRule(therapistId, buildEventRulePayload(values, slot.start))
            }

            return { createdCount: slotsToCreate.length, skippedCount: slots.length - slotsToCreate.length, conflicts }
        },
        {
            successMessage: null,
            onSuccess: (result) => {
                const { createdCount, skippedCount } = result
                if (createdCount === 0) {
                    toast.error('Alle Termine enthalten Konflikte und wurden nicht erstellt.')
                    return
                }

                refreshCalendarData(therapistId)
                if (!options?.suppressModalClose) {
                    setIsAddEventRuleModalOpen?.(false)
                }
                toast.success(
                    skippedCount > 0
                        ? `${createdCount} Termine wurden erstellt, ${skippedCount} mit Konflikten übersprungen.`
                        : `${createdCount} Termine wurden erstellt.`
                )
                options?.onGenerated?.(result)
            }
        }
    )

    return { addGeneratedEventRules, isAddGeneratedEventRulesMutating }
}

function buildEventRulePayload(values: EventRuleForm, start?: DateTime<true>): EventRulePayload {
    const categoryIds = getSelectedEventRuleCategoryIds(values)
    return {
        start: (start ?? getEventRuleStartDateTime(values)).toISO(),
        endDate: values.endDate?.toISODate() || null,
        interval: getEffectiveEventRuleInterval(values),
        categoryIds,
        exceptions: []
    }
}

export function useRefreshCalendarData() {
    const calendarContext = useContext(CalendarContext)
    const hasCalendarContext = 'startDate' in calendarContext && calendarContext.startDate !== undefined
    const startDate = hasCalendarContext ? calendarContext.startDate : null
    const endDate = hasCalendarContext ? calendarContext.endDate : null
    const { mutate } = useSWRConfig()
    return (therapistId: number) => {
        mutate((key) => Array.isArray(key) && key[0] === 'eventRules' && key[1] === therapistId, undefined, {
            revalidate: true
        })
        mutate((key: any) => Array.isArray(key) && key[0] === 'events' && key[1] === therapistId, undefined, {
            revalidate: false
        })
        if (startDate && endDate) {
            mutate(
                (key: any) =>
                    Array.isArray(key) &&
                    key[0] === 'events' &&
                    key[1] === therapistId &&
                    key[2] === startDate.toISO() &&
                    key[3] === endDate.toISO(),
                undefined,
                {
                    revalidate: true
                }
            )
        } else {
            mutate((key: any) => Array.isArray(key) && key[0] === 'events' && key[1] === therapistId, undefined, {
                revalidate: true
            })
        }
    }
}

export function useMoveMeeting() {
    const therapistId = useSelectedTherapistId()
    const { mutate } = useSWRConfig()
    const { trigger: moveMeeting, isMutating: isMoveMeetingMutating } = useTherapistsApiMutation(
        'move-meeting',
        async (api, { slug, start, end }: { slug: string; start: string; end: string }) =>
            api.meetings.updateMeeting(therapistId, slug, {
                start,
                end,
                sendInvitations: false
            }),
        {
            onSuccess: () => {
                void invalidateTherapistMeetingsCache(mutate, therapistId)
            }
        }
    )

    return { moveMeeting, isMoveMeetingMutating }
}

// Types

export interface CalendarConfiguration {
    showWeekends: boolean
    showBookedAppointments: boolean
    showFreeAppointments: boolean
    showBookableInformation: boolean
    currentView: 'timeGridWeek' | 'timeGridDay' | 'dayGridMonth' | 'listWeek'
    startTime: string
    endTime: string
}
